"use client"

import React, { useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
    DialogTrigger
} from '../../../shared/components/ui/dialog'
import { Button } from '../../../shared/components/ui/button'
import { Plus } from 'lucide-react'
import CreateVolunteerForm from '../forms/volunteer-form/create-volunteer-form'
import { cn } from '@/shared/lib/utils'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import type { DefaultServePeriod } from './volunteer-search-combobox'

type AddVolunteerButtonProps = {
    className?: string
    /** When provided (e.g. "Add to Group"), used as the submit button label in the modal */
    submitButtonLabel?: string
    /** When adding from a group's Volunteers tab, pass group id so existing users are linked and new users are created then linked */
    groupId?: string
    /** Allows Group Leader flow to show this button outside Admin/Base Leader roles */
    allowForGroupLeader?: boolean
    /** When adding from a group context, pre-fill the first serve period with the group's dates/region */
    defaultServePeriod?: DefaultServePeriod
    /** Group dashboard "Church / Organization" — prefills org on add; existing users' org still wins on select */
    defaultGroupOrganization?: string
}

function AddVolunteerButton({ className, submitButtonLabel, groupId, allowForGroupLeader = false, defaultServePeriod, defaultGroupOrganization }: AddVolunteerButtonProps) {
    const { session } = useSession()
    const canManageVolunteers =
        session?.role === UserRole.ADMIN ||
        session?.role === UserRole.BASE_LEADER ||
        allowForGroupLeader
    
    const [isOpen, setIsOpen] = useState(false)

    const shouldKeepDialogOpen = (target: EventTarget | null) => {
        if (!(target instanceof HTMLElement)) return false

        // Google Places renders its suggestions outside the dialog portal, so selecting
        // an address would otherwise count as an outside interaction and close the modal.
        return !!target.closest('.pac-container, .pac-item, .pac-item-query')
    }
    
    // Hide button if user doesn't have permission
    if (!canManageVolunteers) {
        return null
    }

    const handleSuccess = () => {
        setIsOpen(false)
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button 
                    variant={"default"}
                    size={"sm"}
                    className={cn('bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10 flex-shrink-0', className)}
                >
                    <Plus className='w-5 h-5' />
                    <span className="hidden sm:inline">Add Volunteers</span>
                </Button>
            </DialogTrigger>
            <DialogContent className={cn(
                'flex flex-col gap-4 py-8 max-w-[600px] max-h-[80vh] w-[90%] md:px-8 md:py-10 overflow-hidden',
                'lg:max-w-5xl xl:max-w-6xl'
            )}
                onInteractOutside={(event) => {
                    if (shouldKeepDialogOpen(event.target)) {
                        event.preventDefault()
                    }
                }}
            >
                <div className="flex shrink-0 flex-col items-center gap-2 text-center">
                    <DialogTitle className='text-2xl'>Add Volunteer(s)</DialogTitle>
                    <DialogDescription>Add required Full Name and Email as well as any other information known.</DialogDescription>
                </div>
                <div
                    className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-1 -mr-1"
                    style={{ WebkitOverflowScrolling: 'touch' }}
                >
                    <CreateVolunteerForm
                        onSuccessCallback={handleSuccess}
                        submitButtonLabel={submitButtonLabel}
                        groupId={groupId}
                        defaultServePeriod={defaultServePeriod}
                        defaultGroupOrganization={defaultGroupOrganization}
                    />
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default AddVolunteerButton